import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getLoginCookieAndSession } from "../utils";
import { getStripeConnectAccountStatus } from "../pages/Seller/seller-setup-api";
import { StripeAccountStatusCode } from "../pages/Seller/seller-setup-types";

// How often to re-check while verification has been submitted but Stripe hasn't approved it yet.
const PENDING_POLL_INTERVAL_MS = 1 * 60 * 1000;

// seller-setup.tsx runs its whole multi-step flow on one URL (/seller-setup), so a route-change
// refetch never fires mid-flow. Call this right after a step changes the account's Stripe status
// (account created, verification submitted) so every mounted useStripeAccountStatus instance —
// including the nav bar, which stays mounted the whole time — picks up the change immediately.
const STRIPE_STATUS_CHANGED_EVENT = "stripe-account-status-changed";
export function notifyStripeAccountStatusChanged() {
  window.dispatchEvent(new Event(STRIPE_STATUS_CHANGED_EVENT));
}

// Fetches the current logged-in user's Stripe connected-account status.
// Used to decide whether "sell" CTAs should route to /sell (approved), /seller-setup (onboarding
// already submitted but still pending Stripe verification or needing action), or /presell (never started),
// and whether to show the "verification pending" message vs. the "not started" empty state.
// While verification is pending, polls every few minutes so the UI updates once Stripe approves —
// polling stops as soon as the account is approved (or if verification was never started).
export const useStripeAccountStatus = () => {
  const location = useLocation();
  const [status, setStatus] = useState<StripeAccountStatusCode | null>(null);
  // Onboarding step from the DB, 1-5 (5 = final verification step submitted) or null if no account yet.
  const [onboardingStep, setOnboardingStep] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  // Components using this hook (e.g. the nav bar) stay mounted across client-side navigation, so a
  // mount-only fetch would keep showing "not started" after the user finishes onboarding elsewhere.
  // Re-fetch on every route change to pick that up, but only show the loading state on the true first fetch.
  const hasFetchedOnceRef = useRef(false);
  const [refreshToken, setRefreshToken] = useState(0);
  // Tracks whether a Stripe Connect account exists, for use inside the poll's catch handler,
  // which only closes over the value from the fetch that scheduled it, not the latest state.
  const startedRef = useRef(false);
  // Countdown (in seconds) to the next background poll, so the UI can show the user we're
  // still actively re-checking rather than looking frozen. Null while not polling.
  const [secondsUntilNextCheck, setSecondsUntilNextCheck] = useState<
    number | null
  >(null);

  useEffect(() => {
    const handler = () => setRefreshToken((t) => t + 1);
    window.addEventListener(STRIPE_STATUS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(STRIPE_STATUS_CHANGED_EVENT, handler);
  }, []);

  useEffect(() => {
    const profileId = getLoginCookieAndSession();
    if (!profileId) {
      setStatus(null);
      setOnboardingStep(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let countdownId: ReturnType<typeof setInterval> | null = null;
    const showLoading = !hasFetchedOnceRef.current;

    const stopCountdown = () => {
      if (countdownId) clearInterval(countdownId);
      countdownId = null;
      setSecondsUntilNextCheck(null);
    };

    const scheduleNextPoll = () => {
      timeoutId = setTimeout(() => fetchStatus(false), PENDING_POLL_INTERVAL_MS);
      setSecondsUntilNextCheck(PENDING_POLL_INTERVAL_MS / 1000);
      if (countdownId) clearInterval(countdownId);
      countdownId = setInterval(() => {
        setSecondsUntilNextCheck((prev) =>
          prev === null || prev <= 1 ? 0 : prev - 1,
        );
      }, 1000);
    };

    const fetchStatus = (isInitial: boolean) => {
      if (isInitial) setLoading(true);
      getStripeConnectAccountStatus(profileId)
        .then((result) => {
          if (cancelled) return;
          // getStripeConnectAccountStatus returns null (404) when no Stripe account exists yet.
          const started = !!result?.stripeAccountId;
          const nextStatus = result?.status ?? "PENDING";
          startedRef.current = started;
          setOnboardingStep(result?.onboardingStep ?? null);
          setStatus(nextStatus);

          // Only keep polling while there's an outstanding review to wait on.
          if (started && nextStatus !== "ACTIVE") {
            scheduleNextPoll();
          } else {
            stopCountdown();
          }
        })
        .catch(() => {
          if (cancelled) return;
          if (isInitial) {
            // No prior data to fall back on — best guess is that verification is pending.
            setStatus("PENDING");
          } else if (startedRef.current) {
            // Transient error on a background poll — keep the last known status on screen
            // (don't clobber e.g. an ACTION_REQUIRED message) and just retry on the next
            // cycle instead of silently freezing the poll loop forever.
            scheduleNextPoll();
          }
        })
        .finally(() => {
          hasFetchedOnceRef.current = true;
          if (!cancelled && isInitial) setLoading(false);
        });
    };

    fetchStatus(showLoading);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (countdownId) clearInterval(countdownId);
    };
  }, [location.pathname, refreshToken]);

  const isApproved = status === "ACTIVE";
  // Step 4 (final review) sets onboardingStep to 5 in the DB when the user submits for
  // verification — it never resets back down, so this stays true even if Stripe later
  // flags something (ACTION_REQUIRED/RESTRICTED) on an already-submitted account.
  // A user who only got partway through onboarding (steps 1-4, account created but never
  // submitted) has onboardingStep < 5 and should still be sent back to /presell, not /seller-setup.
  const hasCompletedOnboarding = onboardingStep === 5;
  // The backend derives ACTION_REQUIRED/RESTRICTED from Stripe's currentlyDue/pastDue/disabledReason
  // fields, which are populated for practically any account mid-onboarding (steps 1-4) — that's
  // expected and not yet actionable by the user. Only surface it once they've actually submitted.
  const needsAction =
    hasCompletedOnboarding &&
    (status === "ACTION_REQUIRED" || status === "RESTRICTED");
  // Gate on hasCompletedOnboarding, not hasStartedVerification: a Stripe Connect account row
  // (and thus hasStartedVerification) is created as soon as step 1 is submitted, so a user who
  // abandons onboarding partway through would otherwise still see "verification pending" instead
  // of the original "not started" empty state.
  const isVerificationPending =
    hasCompletedOnboarding && !isApproved && !needsAction;
  const sellRoute = isApproved
    ? "/sell"
    : hasCompletedOnboarding
      ? "/seller-setup"
      : "/presell";

  return {
    status,
    loading,
    isApproved,
    isVerificationPending,
    needsAction,
    sellRoute,
    secondsUntilNextCheck,
  };
};
